import {
  addIndex,
  always,
  complement,
  compose,
  defaultTo,
  ifElse,
  is,
  isEmpty,
  map,
  multiply,
  path,
  pathEq,
  pipe,
  prop,
  slice,
  toPairs,
  values,
  when,
} from 'ramda';
import { select } from 'redux-saga/effects';
import { decamelizeKeys } from 'humps';
import { selectExperienceById, selectExperiencesRegions, selectMarkets, selectVendors } from '../contentRegistry';
import { selectCurrentExperienceId } from '../experience';
import { selectBookingTickets } from '../booking';
import { TICKET_TYPES } from '../booking/booking.constants';
import { BookingCurrency } from '../../shared/services/booking/currency';

export const addLeadingSlashWhenExists = pipe(
  defaultTo(''),
  when(complement(isEmpty), string => `/${string}`)
);

export const sliceExperiences = (startIndex, endIndex) =>
  pipe(
    values,
    slice(startIndex, endIndex + 1)
  );

export const getNameById = (id, shouldAddSlash = true) =>
  pipe(
    path([id, 'name']),
    when(always(shouldAddSlash), addLeadingSlashWhenExists)
  );

export const getBrand = (vendor, vendors) =>
  ifElse(is(Object), path(['fields', 'companyName']), id =>
    pipe(
      prop(id),
      defaultTo({}),
      prop('companyName')
    )(vendors)
  )(vendor);

export const updateExperiencesPosition = startIndex =>
  addIndex(map)((experience, index) => ({
    ...experience,
    position: index + startIndex + 1,
  }));

export const getCategory = (market, region) => `experiences${market}${region}`;

export function* getExperiencePropertiesForGTM(experience) {
  const markets = yield select(selectMarkets);
  const vendors = yield select(selectVendors);
  const experiencesRegions = yield select(selectExperiencesRegions);

  if (!experience) {
    return null;
  }

  const { vendor, market: marketId, id, name } = experience;

  const market = getNameById(marketId)(markets);
  const region = getNameById(id)(experiencesRegions);
  const brand = getBrand(vendor, vendors);
  const category = getCategory(market, region);

  return {
    brand,
    category,
    name,
    id,
  };
}

export const getIntercomPriceObject = price => ({ amount: parseFloat(price * 100), currency: BookingCurrency.USD });

const getPriceByTypeWhenEnabled = (type, { tickets }) => {
  const { enabled, price } = tickets.types[type];
  if (!enabled || tickets.isGroup) {
    return undefined;
  }

  return getIntercomPriceObject(price);
};

export function* getExperiencePropertiesForIntercom(experience) {
  const markets = yield select(selectMarkets);
  const vendors = yield select(selectVendors);
  const experiencesRegions = yield select(selectExperiencesRegions);

  if (!experience) {
    return null;
  }

  const { vendor, market: marketId, id, name } = experience;

  const market = getNameById(marketId, false)(markets);
  const region = getNameById(id, false)(experiencesRegions);
  const brand = getBrand(vendor, vendors);
  const adultPrice = getPriceByTypeWhenEnabled(TICKET_TYPES.Adult, experience);
  const childPrice = getPriceByTypeWhenEnabled(TICKET_TYPES.Child, experience);

  return decamelizeKeys({
    provider: brand,
    region,
    market,
    experience: name,
    adultPrice,
    childPrice,
  });
}

export const getTicketPriceByType = (experience, type, count) =>
  ifElse(
    pathEq(['tickets', 'isGroup'], true),
    always(undefined),
    compose(
      multiply(count),
      parseFloat,
      path(['tickets', 'types', type, 'price'])
    )
  )(experience);

export function* getCheckoutProducts() {
  const experienceId = yield select(selectCurrentExperienceId);
  const experience = yield select(selectExperienceById(experienceId));
  const properties = yield getExperiencePropertiesForGTM(experience);
  const selectedTickets = yield select(selectBookingTickets);

  return pipe(
    toPairs,
    map(([variant, quantity]) => ({
      ...properties,
      variant,
      quantity,
      price: getTicketPriceByType(experience, variant, quantity),
    }))
  )(selectedTickets);
}
